import { useState, useEffect } from 'react';
import { useApiContext } from '@/contexts/apiContext';
import { Box, Card, CardContent, Stack, Typography, CircularProgress, IconButton } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { formatDistanceToNow } from 'date-fns';
const LiveActivityFeed = () => {
  const apiContext = useApiContext();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchLogs = async () => {
    try {
      const [auditRecords, activityRecords] = await Promise.all([
        apiContext.getRecentAuditRecords(15),
        apiContext.getActivityFeed(15).catch(() => []) // Gracefully handle if endpoint not ready
      ]);

      const formattedAudit = (auditRecords || []).map((r: any) => {
        const dateObj = new Date(r.performedTime);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const timeStr = isNaN(dateObj.getTime())
          ? '--/-- --:--:--'
          : `${month}/${day} ${dateObj.toTimeString().split(' ')[0]}`;

        let actionMsg = '';
        const entityName = r.newRecord?.name || r.oldRecord?.name || r.entityId || 'Entity';
        const actionType = r.action || 'Performed action';

        let typeLabel = r.entityType || '';
        if (typeLabel.endsWith('Instance')) {
          typeLabel = typeLabel.replace('Instance', ' Instance');
        }

        actionMsg = `${r.performedBy || 'System'} ${actionType.toLowerCase()}d ${typeLabel} "${entityName}"`;

        let type: 'info' | 'success' | 'warning' = 'info';
        if (actionType.toLowerCase() === 'create') {
          type = 'success';
        } else if (actionType.toLowerCase() === 'delete') {
          type = 'warning';
        }

        return {
          id: `audit-${r.id}`,
          timestamp: dateObj.getTime(),
          time: timeStr,
          text: actionMsg,
          type,
        };
      });

      const formattedActivity = (activityRecords || []).map((r: any) => {
        const dateObj = new Date(r.timestamp);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const timeStr = isNaN(dateObj.getTime())
          ? '--/-- --:--:--'
          : `${month}/${day} ${dateObj.toTimeString().split(' ')[0]}`;

        let typeLabel = r.entityType || '';
        if (typeLabel.endsWith('Instance')) {
          typeLabel = typeLabel.replace('Instance', ' Instance');
        }
        const entityName = r.entityName || r.entityId || 'Entity';
        const actionMsg = `System event: ${r.eventDefId} for ${typeLabel} "${entityName}"`;

        return {
          id: `act-${r.id}`,
          timestamp: dateObj.getTime(),
          time: timeStr,
          text: actionMsg,
          type: 'info' as const,
        };
      });

      const allRecords = [...formattedAudit, ...formattedActivity].sort((a, b) => b.timestamp - a.timestamp);
      setLogs(allRecords);
    } catch (e) {
      console.error('Failed to fetch recent audit records', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 10000); // Poll every 10s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'success':
        return '#10b981';
      case 'warning':
        return '#ef4444';
      case 'info':
      default:
        return '#3b82f6';
    }
  };

  return (
    <Card
      className="glass-card-light"
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.4)',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 1.5, pb: '12px !important', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Live Activity Feed
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#10b981',
                animation: 'pulse-live 1.2s infinite ease-in-out',
                '@keyframes pulse-live': {
                  '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                  '50%': { transform: 'scale(1.3)', opacity: 1 },
                  '100%': { transform: 'scale(0.8)', opacity: 0.5 },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontSize: '9px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
            >
              Streaming
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
          <Stack spacing={1}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={16} />
              </Box>
            ) : logs.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}>
                No recent activity.
              </Typography>
            ) : (
              logs.map((log) => (
                <Box
                  key={log.id}
                  sx={{
                    p: 0.75,
                    borderRadius: '6px',
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    borderLeft: `3px solid ${getStatusColor(log.type)}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 650,
                      color: 'text.secondary',
                      fontSize: '9px',
                      pt: 0.1,
                    }}
                  >
                    [{log.time}]
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.primary', fontSize: '10px', fontWeight: 550, lineHeight: 1.2 }}
                  >
                    {log.text}
                  </Typography>
                </Box>
              ))
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};
export default LiveActivityFeed;
